/**
 * Address Entry Page - Address collection step
 *
 * Screen for entering address with search functionality and manual entry option
 * Part of the sequential flow: Calculator → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, MapPin } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { z } from "zod";
import { RampsButton } from "@/components/ramps/Button";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useContinue } from "@/hooks/useContinue";
import { FEATURE_FLAGS, useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";
import { useAnalytics } from "@/hooks/usePostHog";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { ADDRESS_LINE1_MIN } from "@/utils/addressLines";
import { captureException } from "@/utils/error-tracking";
import { extractAddressComponents } from "@/utils/google-places-autocomplete";
import { RampsInput } from "../components/ramps/Input";
import { RampsSelectBottomSheet } from "../components/ramps/RampsSelectBottomSheet";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import {
	findSubdivisionByRegionName,
	getSubdivisions,
} from "../constants/subdivisions";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

interface CountryOption {
	value: string;
	label: string;
	countryCode: string;
}

// Zod schema for address validation with custom error messages
const createAddressSchema = (isUSA: boolean) => {
	const postalLabel = isUSA ? t`ZIP code` : t`Postal code`;
	return z.object({
		address: z
			.string()
			.trim()
			.min(
				ADDRESS_LINE1_MIN,
				t`Address must be at least ${ADDRESS_LINE1_MIN} characters`,
			)
			.max(30, t`Address must be 30 characters or less`)
			.regex(
				/^([a-zA-Z0-9 \u00C0-\u017F#/."',()-])*$/,
				t`Only alpha, numeric and certain special characters (#./'-,()") are allowed`,
			),
		address2: z
			.string()
			.trim()
			.max(80, t`Address Line 2 must be 80 characters or less`)
			.regex(
				/^([a-zA-Z0-9 \u00C0-\u017F#/."',()-])*$/,
				t`Only alpha, numeric and certain special characters (#./'-,()") are allowed`,
			)
			.optional()
			.or(z.literal("")),
		city: z
			.string()
			.trim()
			.min(1, t`City is required`)
			.max(40, t`City must be 40 characters or less`)
			.regex(
				/^([a-zA-Z \u00C0-\u017F#/."',()-])*$/,
				t`Only alpha and certain special characters (#./'-,()") are allowed`,
			),
		zipCode: z
			.string()
			.trim()
			.min(2, t`${postalLabel} must be at least 2 characters`)
			.max(10, t`${postalLabel} must be 10 characters or less`)
			.regex(
				/^([0-9a-zA-Z -])*$/,
				t`Only alphanumeric characters, spaces, and hyphens are allowed`,
			),
		state: z.string().optional(),
	});
};

function AddressPage() {
	const search = Route.useSearch(); // Get current search params
	const store = useRampsStore();
	const handleContinue = useContinue();
	const handleBack = useNavigateBack();
	const { trackPageView } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("address", { page_name: "address" });
	}, [trackPageView]);

	// Generate unique IDs for form fields
	const addressId = useId();
	const address2Id = useId();
	const cityId = useId();
	const zipCodeId = useId();

	// Get API client with authentication (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Fetch countries using same logic as calculator and phone
	const countriesQuery = useQuery({
		queryKey: ["countries"],
		queryFn: async () => {
			const countries = await apiClient.referenceData.getCountries();
			return countries;
		},
		enabled: false,
	});

	// Transform API data for address country selection.
	// Deliberately NOT filtered by cashOutCountries: a customer can live in a
	// country Ramps doesn't pay out to, and address autocomplete already resolves
	// against the full country list below (RAMPS-4425).
	const allCountries = countriesQuery.data || [];

	const countryOptions: CountryOption[] = allCountries.map((country) => ({
		value: country.iso3Code,
		label: country.name,
		countryCode: country.iso2Code.toLowerCase(),
	}));

	// Check feature flag for address lookup
	const { isFeatureEnabled } = useFeatureFlags();
	const isAddressLookupEnabled = isFeatureEnabled(
		FEATURE_FLAGS.RAMPS_ADDRESS_LOOKUP_ENABLED,
	);

	// Determine default mode: if address fields exist, default to manual; otherwise autocomplete
	// If feature flag is disabled, always use manual mode
	const hasExistingAddress =
		store.address || store.city || store.zipCode || store.addressCountry;
	const [mode, setMode] = useState<"autocomplete" | "manual">(
		!isAddressLookupEnabled || hasExistingAddress ? "manual" : "autocomplete",
	);

	// State variables
	const [address, setAddress] = useState(store.address);
	const [address2, setAddress2] = useState(store.address2);
	const [city, setCity] = useState(store.city);
	const [zipCode, setZipCode] = useState(store.zipCode);
	const [country, setCountry] = useState(
		store.addressCountry || store.payoutCountry,
	);
	const [state, setState] = useState(store.addressState);
	const [validationErrors, setValidationErrors] = useState<
		Record<string, string>
	>({});

	const selectedCountry = countryOptions.find((c) => c.value === country);

	// Get ISO2 country code for autocomplete region bias
	const selectedCountryISO2 = selectedCountry?.countryCode.toUpperCase();

	// Places autocomplete hook - only initialize if feature flag is enabled
	const {
		inputValue,
		setInputValue,
		suggestions,
		isLoading: isAutocompleteLoading,
		error: autocompleteError,
		selectPlace,
		clear: clearAutocomplete,
	} = usePlacesAutocomplete({
		regionCode: selectedCountryISO2,
		languageCode: "en",
		minInputLength: 3,
		maxSuggestions: 5,
	});

	// Countries that require state selection
	const countriesWithStates = ["USA", "CAN", "MEX"];
	const shouldShowState = countriesWithStates.includes(country);

	// Helper function to prevent numbers in text inputs
	const preventNumbers = (value: string) => {
		return value.replace(/[0-9]/g, "");
	};

	// Real-time validation for address fields
	const validateField = (fieldName: string, value: string) => {
		try {
			const addressSchema = createAddressSchema(country === "USA");
			const fieldSchema =
				addressSchema.shape[fieldName as keyof typeof addressSchema.shape];
			if (fieldSchema) {
				fieldSchema.parse(value);
				// Clear error if validation passes
				setValidationErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[fieldName];
					return newErrors;
				});
			}
		} catch (error) {
			if (error instanceof z.ZodError) {
				setValidationErrors((prev) => ({
					...prev,
					[fieldName]: error.issues[0]?.message || t`Invalid value`,
				}));
			}
		}
	};

	// Clear state when switching to a country that doesn't require states
	useEffect(() => {
		if (!shouldShowState) {
			setState("");
		}
	}, [shouldShowState]); // Trigger when shouldShowState changes

	const handleSubmit = () => {
		// Clear previous validation errors
		setValidationErrors({});

		// Validate with Zod schema (created fresh to get latest translations)
		const addressSchema = createAddressSchema(country === "USA");
		const validationData = {
			address,
			address2: address2 || undefined,
			city,
			zipCode,
			state: shouldShowState ? state : undefined,
		};

		const result = addressSchema.safeParse(validationData);

		if (!result.success) {
			// Set validation errors
			const errors: Record<string, string> = {};
			result.error.issues.forEach((err: z.ZodIssue) => {
				if (err.path[0]) {
					errors[err.path[0].toString()] = err.message;
				}
			});
			setValidationErrors(errors);
			return;
		}

		// Validate required fields - state is only required for certain countries
		const isValidForm =
			address && city && zipCode && (shouldShowState ? state : true);

		if (!isValidForm) {
			return;
		}

		// Save the trimmed values from the schema, not the raw input. Core trims
		// before validating, so "Reno " must not be accepted as 5 characters.
		saveAndContinue({
			address: result.data.address,
			address2: result.data.address2 || "",
			city: result.data.city,
			zipCode: result.data.zipCode,
			addressCountry: country,
			addressState: shouldShowState ? state : "",
		});
	};

	const saveAndContinue = (addressData: {
		address: string;
		address2?: string;
		city: string;
		zipCode: string;
		addressCountry: string;
		addressState: string;
	}) => {
		rampsActions.setAddressData({
			...addressData,
			isManualAddress: mode === "manual",
		});
		rampsActions.setCurrentStep("date-of-birth");

		setTimeout(() => {
			handleContinue(search);
		}, 2000);
	};

	const handleCountryChange = (value: string) => {
		setCountry(value);
		setState(""); // Clear state when country changes
	};

	// Handle place selection from autocomplete
	const handlePlaceSelect = async (
		prediction: Parameters<typeof selectPlace>[0],
	) => {
		try {
			const placeDetails = await selectPlace(prediction);
			if (!placeDetails) {
				return;
			}

			// Extract address components
			const extractedAddress = extractAddressComponents(
				placeDetails,
				allCountries,
				findSubdivisionByRegionName,
			);

			if (!extractedAddress) {
				console.error("Failed to extract address components");
				return;
			}

			// Save directly to store
			rampsActions.setAddressData({
				address: extractedAddress.address,
				address2: extractedAddress.address2,
				city: extractedAddress.city,
				zipCode: extractedAddress.zipCode,
				addressCountry: extractedAddress.addressCountry,
				addressState: extractedAddress.addressState,
				isManualAddress: false,
			});

			// Also update local state for Continue button validation
			setAddress(extractedAddress.address);
			setAddress2(extractedAddress.address2 || "");
			setCity(extractedAddress.city);
			setZipCode(extractedAddress.zipCode);
			setCountry(extractedAddress.addressCountry);
			setState(extractedAddress.addressState);

			// Clear validation errors
			setValidationErrors({});

			// Clear autocomplete input but stay in autocomplete mode
			clearAutocomplete();
		} catch (error) {
			captureException(error, {
				tags: { component: "AddressPage", action: "handlePlaceSelect" },
			});
			console.error("Error selecting place:", error);
		}
	};

	// Switch to manual mode
	const switchToManualMode = () => {
		setMode("manual");
		clearAutocomplete();
	};

	// Switch to autocomplete mode
	const switchToAutocompleteMode = () => {
		setMode("autocomplete");
		clearAutocomplete();
	};

	return (
		<div className="flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
					aria-label="Back"
				>
					<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-8 px-4">
				<Progress
					value={60}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-4">
				<div className="mb-8 text-left">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Your address`}
					</RampsText>
				</div>

				{/* Autocomplete Mode */}
				{mode === "autocomplete" && isAddressLookupEnabled && (
					<div className="flex-1 space-y-4">
						{/* Search Input */}
						<RampsInput
							type="text"
							label={t`Enter your address`}
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							error={autocompleteError?.message}
						/>

						{/* Show selected address if one was chosen */}
						{address && city && zipCode && !inputValue && (
							<div className="rounded-lg bg-ramps-background-surface-2 px-4 py-3">
								<RampsText variant="body-sm" color="secondary" className="mb-1">
									{t`Selected address`}
								</RampsText>
								<RampsText variant="body-default" color="primary">
									{address}
									{address2 && `, ${address2}`}
									{`, ${city}, ${zipCode}`}
									{state &&
										`, ${getSubdivisions(country).find((s) => s.value === state)?.label || state}`}
								</RampsText>
							</div>
						)}

						{/* Suggestions List */}
						{inputValue.length >= 3 && (
							<div className="space-y-2">
								{isAutocompleteLoading && (
									<div className="px-4 py-2">
										<RampsText variant="body-default" color="secondary">
											{t`Loading suggestions...`}
										</RampsText>
									</div>
								)}
								{!isAutocompleteLoading && suggestions.length > 0 && (
									<div className="divide-y divide-ramps-border-default rounded-lg border border-ramps-border-default bg-ramps-background-base">
										{suggestions.map((suggestion) => (
											<button
												key={suggestion.place_id}
												type="button"
												onClick={() => handlePlaceSelect(suggestion)}
												className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-ramps-background-surface-2"
											>
												<MapPin className="h-5 w-5 shrink-0 text-ramps-text-secondary" />
												<div className="min-w-0 flex-1">
													<RampsText
														variant="body-default"
														color="primary"
														className="font-medium"
													>
														{suggestion.structured_formatting?.main_text ||
															suggestion.description}
													</RampsText>
													{suggestion.structured_formatting?.secondary_text && (
														<RampsText
															variant="body-sm"
															color="secondary"
															className="mt-1"
														>
															{suggestion.structured_formatting.secondary_text}
														</RampsText>
													)}
												</div>
											</button>
										))}
									</div>
								)}
								{!isAutocompleteLoading &&
									inputValue.length >= 3 &&
									suggestions.length === 0 && (
										<div className="px-4 py-2">
											<RampsText variant="body-default" color="secondary">
												{t`No addresses found`}
											</RampsText>
										</div>
									)}
							</div>
						)}

						{/* Manual Entry Option */}
						{isAddressLookupEnabled && (
							<div className="pt-4 text-center">
								<button
									type="button"
									onClick={switchToManualMode}
									className="font-medium text-ramps-text-primary hover:underline"
								>
									<RampsText variant="body-default" color="primary">
										{t`Enter address manually`}
									</RampsText>
								</button>
							</div>
						)}
					</div>
				)}

				{/* Manual Address Entry Mode */}
				{mode === "manual" && (
					<div className="flex-1 space-y-6">
						{/* Switch to Autocomplete Mode Button */}
						{isAddressLookupEnabled && (
							<div className="mb-4 text-center">
								<button
									type="button"
									onClick={switchToAutocompleteMode}
									className="font-bold text-ramps-text-primary underline hover:no-underline"
								>
									<RampsText
										variant="body-default"
										color="primary"
										className="font-bold underline"
									>
										{t`Use address lookup`}
									</RampsText>
								</button>
							</div>
						)}
						<div className="space-y-4">
							{/* Address Line 1 */}
							<RampsInput
								id={addressId}
								type="text"
								label={t`Address Line 1`}
								value={address}
								onChange={(e) => {
									const newValue = e.target.value;
									setAddress(newValue);
									validateField("address", newValue);
								}}
								error={validationErrors.address}
							/>

							{/* Address Line 2 (Optional) */}
							<RampsInput
								id={address2Id}
								type="text"
								label={t`Address Line 2 (Optional)`}
								value={address2}
								onChange={(e) => {
									const newValue = e.target.value;
									setAddress2(newValue);
									validateField("address2", newValue || "");
								}}
								error={validationErrors.address2}
							/>

							{/* City */}
							<RampsInput
								id={cityId}
								type="text"
								label={t`City`}
								value={city}
								onChange={(e) => {
									const newValue = preventNumbers(e.target.value);
									setCity(newValue);
									validateField("city", newValue);
								}}
								error={validationErrors.city}
							/>

							{/* Postal code */}
							<RampsInput
								id={zipCodeId}
								type="text"
								label={country === "USA" ? t`ZIP code` : t`Postal code`}
								value={zipCode}
								onChange={(e) => {
									const newValue = e.target.value;
									setZipCode(newValue);
									validateField("zipCode", newValue);
								}}
								error={validationErrors.zipCode}
							/>

							{/* Country */}
							{countriesQuery.isLoading ? (
								<div className="rounded-lg bg-ramps-background-surface-2 px-4 pt-6 pb-2">
									<RampsText
										variant="body-sm"
										color="secondary"
										className="mb-0.5 text-left"
									>
										{t`Country`}
									</RampsText>
									<RampsText
										variant="body-default"
										color="secondary"
										className="min-h-6"
									>
										{t`Loading countries...`}
									</RampsText>
								</div>
							) : countriesQuery.error || countryOptions.length === 0 ? (
								<div className="rounded-lg bg-ramps-background-surface-2 px-4 pt-6 pb-2">
									<RampsText
										variant="body-sm"
										color="secondary"
										className="mb-0.5 text-left"
									>
										{t`Country`}
									</RampsText>
									<RampsText
										variant="body-default"
										color="secondary"
										className="min-h-6"
									>
										{countriesQuery.error
											? t`Error loading countries`
											: t`No countries available`}
									</RampsText>
								</div>
							) : (
								<RampsSelectBottomSheet
									label={t`Country`}
									sheetTitle={t`Select country`}
									options={countryOptions.map((countryOption) => ({
										value: countryOption.value,
										label: countryOption.label,
										countryCode: countryOption.countryCode,
									}))}
									value={country}
									onSelect={(value) => handleCountryChange(value)}
									size="full"
									searchPlaceholder={t`Search countries...`}
									renderOptionIcon={(option) =>
										option.countryCode ? (
											<CircleFlag
												countryCode={option.countryCode}
												className="h-6 w-6"
											/>
										) : null
									}
									placeholder={t`Select country`}
								/>
							)}

							{/* State - Only show for countries that have states */}
							{shouldShowState && (
								<RampsSelectBottomSheet
									label={t`State`}
									sheetTitle={
										country === "USA" ? t`Select state` : t`Select region`
									}
									options={getSubdivisions(country).map((stateOption) => ({
										value: stateOption.value,
										label: stateOption.label,
									}))}
									value={state}
									onSelect={(value) => setState(value)}
									size="full"
									searchPlaceholder={t`Search...`}
									placeholder={t`Select state`}
								/>
							)}
						</div>
					</div>
				)}

				{/* Continue Button */}
				<div className="pt-8 pb-6">
					<RampsButton
						onClick={handleSubmit}
						disabled={
							!address ||
							!city ||
							!zipCode ||
							(shouldShowState && !state) ||
							Object.keys(validationErrors).length > 0
						}
						className="h-12 w-full rounded-lg bg-black font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{t`Continue`}
					</RampsButton>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/address")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: AddressPage,
});
